import { Building2, Building, Briefcase, GitBranch, PanelLeftClose, PanelLeftOpen, User, Circle, StickyNote, BarChart3, Cog, LayoutTemplate, Layers, MousePointer, Columns, PanelBottom, FormInput, List, Receipt, Type, SwatchBook } from "lucide-react";
import { useState } from "react";

const nodeTemplates = [
  { type: "company", label: "Entreprise", icon: Building2, color: "#6366F1", description: "Holding, societe", group: "éléments" },
  { type: "subsidiary", label: "Filiale", icon: Building, color: "#3B82F6", description: "Branche, division", group: "éléments" },
  { type: "person", label: "Personne", icon: User, color: "#7C3AED", description: "Membre, contact", group: "éléments" },
  { type: "logo", label: "Logo", icon: Circle, color: "#6366F1", description: "Marque, outil", group: "éléments" },
  { type: "service", label: "Service", icon: Cog, color: "#0EA5E9", description: "API, produit", group: "éléments" },
  { type: "job", label: "Metier", icon: Briefcase, color: "#F59E0B", description: "Poste, fonction", group: "éléments" },
  { type: "scenario", label: "Scenario", icon: GitBranch, color: "#F43F5E", description: "Condition si/alors", group: "éléments" },
  { type: "note", label: "Note", icon: StickyNote, color: "#F59E0B", description: "Post-it, memo", group: "éléments" },
  { type: "metric", label: "KPI", icon: BarChart3, color: "#10B981", description: "Chiffre cle", group: "éléments" },
  { type: "info", label: "Info", icon: List, color: "#6366F1", description: "Bloc avec listes", group: "éléments" },
  { type: "price", label: "Tarif", icon: Receipt, color: "#10B981", description: "Prix HT / TTC", group: "éléments" },
  { type: "label", label: "Etiquette", icon: Type, color: "#1F2937", description: "Titre, annotation", group: "éléments" },
  { type: "legend", label: "Legende", icon: SwatchBook, color: "#8B5CF6", description: "Legende couleurs", group: "éléments" },
  // Web blocks
  { type: "header", label: "Header", icon: LayoutTemplate, color: "#6366F1", description: "Navigation, menu", group: "web" },
  { type: "hero", label: "Hero", icon: Layers, color: "#7C3AED", description: "Bannière principale", group: "web" },
  { type: "cta", label: "CTA", icon: MousePointer, color: "#F43F5E", description: "Bouton d'action", group: "web" },
  { type: "section", label: "Section", icon: Columns, color: "#3B82F6", description: "Contenu 1-3 col", group: "web" },
  { type: "footer", label: "Footer", icon: PanelBottom, color: "#6B7280", description: "Pied de page", group: "web" },
  { type: "form", label: "Formulaire", icon: FormInput, color: "#0EA5E9", description: "Champs, envoi", group: "web" },
];

export default function Toolbox() {
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const [activeGroup, setActiveGroup] = useState("éléments");

  const onDragStart = (e, type) => {
    e.dataTransfer.setData("application/reactflow", type);
    e.dataTransfer.effectAllowed = "move";
  };

  const filteredTemplates = nodeTemplates.filter(t => t.group === activeGroup);

  if (collapsed) {
    return (
      <div className="w-12 bg-white border-r border-gray-200 h-full flex flex-col items-center py-3 gap-2 shrink-0" data-testid="toolbox-sidebar">
        <button
          onClick={() => setCollapsed(false)}
          className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 mb-2"
          data-testid="toolbox-expand-btn"
        >
          <PanelLeftOpen size={16} />
        </button>
        {nodeTemplates.filter(t => t.group === activeGroup).map((tpl) => {
          const Icon = tpl.icon;
          return (
            <div
              key={tpl.type}
              draggable
              onDragStart={(e) => onDragStart(e, tpl.type)}
              className="w-9 h-9 rounded-lg flex items-center justify-center cursor-grab active:cursor-grabbing hover:scale-110 transition-transform"
              style={{ backgroundColor: tpl.color + "15" }}
              title={tpl.label}
              data-testid={`toolbox-${tpl.type}`}
            >
              <Icon size={16} style={{ color: tpl.color }} />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="w-60 bg-gray-50/80 border-r border-gray-200 h-full flex flex-col overflow-hidden shrink-0"
      data-testid="toolbox-sidebar"
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setActiveGroup("éléments")}
            className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${activeGroup === "éléments" ? "bg-indigo-100 text-indigo-700" : "text-gray-400 hover:text-gray-600"}`}
            data-testid="toolbox-tab-éléments"
          >
            Éléments
          </button>
          <button
            onClick={() => setActiveGroup("web")}
            className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${activeGroup === "web" ? "bg-blue-100 text-blue-700" : "text-gray-400 hover:text-gray-600"}`}
            data-testid="toolbox-tab-web"
          >
            Web Blocs
          </button>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          className="w-6 h-6 rounded-md hover:bg-gray-200 flex items-center justify-center text-gray-400"
          data-testid="toolbox-collapse-btn"
        >
          <PanelLeftClose size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {filteredTemplates.map((tpl) => {
          const Icon = tpl.icon;
          return (
            <div
              key={tpl.type}
              draggable
              onDragStart={(e) => onDragStart(e, tpl.type)}
              className="bg-white rounded-xl border border-gray-200 p-3 cursor-grab hover:shadow-md hover:border-gray-300 active:cursor-grabbing active:scale-[0.97] flex items-center gap-3 transition-all duration-150"
              data-testid={`toolbox-${tpl.type}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: tpl.color + "15" }}
              >
                <Icon size={18} style={{ color: tpl.color }} />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-sm text-gray-900 leading-tight">
                  {tpl.label}
                </span>
                <span className="text-[10px] text-gray-400 leading-tight truncate">
                  {tpl.description}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-3 border-t border-gray-200/60">
        <p className="text-[10px] text-gray-400 leading-relaxed">
          Glissez un élément sur le canvas pour l'ajouter. Connectéz les
          éléments en glissant entre les points.
        </p>
      </div>
    </div>
  );
}
